import React, { Component } from 'react';
import Header from '../../components/Header';
import FormLabel from '../../components/FormLabel';
import FormField from '../../components/FormField';
import StickyHeader from '../../components/StickyHeader/StickyHeader';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { updateName, updatePassword, updateEmail, checkCredentials, resetPassword } from 'redux/modules/account';
import i18n from 'i18n/i18n';
import _ from 'lodash';
import Typography from '@material-ui/core/Typography';
import ValidationSchema from './AccountDetailsSchema';
import { isAdmin } from 'utils/state';
import { focusSelect } from 'utils/forms';
import { getHost, makeQuery } from 'utils/browser';
import { replaceConsecutiveSpaces } from 'utils/string';
import { history, setConfirmationFunction } from 'redux/store';
import { showModal, hideModal } from 'redux/modules/modal';
import CenteredContent from 'components/CenteredContent/CenteredContent';
import ERRORS from '../../errors/errorids';
import { ACCOUNT_SETTINGS } from '../../config';
import { SpinnerIcon, CheckIcon } from '../../theme/icons';

const localize = i18n.loc.localize;

const minPasswordLength = ACCOUNT_SETTINGS.minPasswordLength;
const maxPasswordLength = ACCOUNT_SETTINGS.maxPasswordLength;

const styles = theme => {
	return {
		header: {
			paddingBottom: 20
		},
		button: {
			fontSize: 16,
			lineHeight: 1.5,
			width: 160
		},
		detailsContainer: {
			marginTop: theme.spacing.unit * 2,
		},
		columnField: {
			width: 260
		},
		columnSaveButton: {
		},
		field: {
			width: 240,
			margin: '0 0 5px 0',
			'&:focus': {
				outline: 'none'
			}
		},
		'root': {
			'&$error': {
				marginTop: 10,
				marginBottom: 15,
				fontSize: '0.8rem'
			}
		},
		fieldDescription: {
			width: 400,
			marginLeft: 18,
			marginTop: 20,
			marginBottom: 10
		},
		emailSentMessage: {
			width: 600,
			marginLeft: 18,
			marginTop: 20,
			marginBottom: 10
		},
		spinnerIcon: {
			height: '24px',
			width: '24px',
			fill: 'white',
			animation: 'spin 2s linear infinite'
		},
		checkIcon: {
			color: 'green'
		},
		'@keyframes spin': {
			'0': {
				transform: 'rotate(0deg)'
			},			
			'100%': {
				transform: 'rotate(360deg)'
			}
		}	};
};

const onLeaveMessage = localize('leave-page-confirmation');

const errors = {
	[ERRORS.OFFLINE] : localize('account-error-offline'),
	[ERRORS.INVALID_PASSWORD] : localize('account-error-invalid-password'),
	[ERRORS.PASSWORDS_NOT_MATCHING] : localize('account-error-passwords-not-matching'),
	[ERRORS.PASSWORD_ALREADY_USED] : localize('account-error-password-already-used'),
	[ERRORS.WRONG_PASSWORD] : localize('account-error-wrong-password'),
	[ERRORS.EMAIL_ALREADY_USED] : localize('account-error-email-already-used'),
	[ERRORS.INVALID_EMAIL_FORMAT] : localize('account-error-invalid-email-format'),
	[ERRORS.INVALID_FIRST_NAME] : localize('account-error-invalid-first-name'),
	[ERRORS.INVALID_LAST_NAME] : localize('account-error-invalid-last-name'),
	[ERRORS.EMAIL_ASSOCIATED_TO_ANOTHER_USER] : localize('account-error-email-associated-to-another-user'),
	[ERRORS.UNKNOWN_EMAIL] : localize('account-error-unknown-email'),
	[ERRORS.NOTIFICATION_SERVICE_INTERNAL_ERROR] : localize('account-error-notification-service-internal-error')
};

@withStyles(styles)
class AccountDetails extends Component {

	constructor(props) {
		super(props);
		const currentEmail = _.get(props.user, 'mainEmail');
		const currentFirstName = _.get(props.user, 'firstName');
		const currentLastName = _.get(props.user, 'lastName');

		this.currentPassword = React.createRef();
		this.newEmail = React.createRef();
		this.newFirstName = React.createRef();
		this.newLastName = React.createRef();
		this.newPassword = React.createRef();
		
		this.state = {
			// email
			currentEmail: currentEmail,
			newEmail: currentEmail,
			oldEmail: '',
			newEmailSuccess: false,
			newEmailError: '',

			// name
			newNameSuccess: false,
			newNameError: false,
			newFirstName: currentFirstName,
			newLastName: currentLastName,

			// password
			currentPassword: '',
			passwordError: '',
			checkedPassword: '',
			newPassword: '',
			newPasswordSuccess: false,
			newPasswordError: '',
			confirmNewPassword: '',

			// misc flags
			credentialsChecked: false,
			unlocking: false,
			changingEmail: false,
			changingFirstName: false,
			changingLastName: false,
			changingPassword: false,
			hasPassword: this.props.user.hasPassword,
			showResetPasswordSuccess: false,
			showResetPasswordError: false,

			touched: {},
			errors: {}
		};
		this.onBeforeUnload = this.onBeforeUnload.bind(this);
	}

	componentDidMount() {
		setConfirmationFunction((message, callback) => {
			this.doIfCanLeave(callback);
		});
		this.historyUnblock = history.block('');

		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	componentWillUnmount() {
		this.historyUnblock();
		setConfirmationFunction(null);

		this.currentPassword = null;
		this.newEmail = null;
		this.newFirstName = null;
		this.newLastName = null;
		this.newPassword = null;

		window.removeEventListener('beforeunload', this.onBeforeUnload);
	}

	handleCredentialsValidation(nextProps) {
		if (nextProps.account.areCredentialsValid === true) {
			this.setState({
				checkedPassword: this.state.currentPassword,
				credentialsChecked: true,
				unlocking: false,
				errors: { 'currentPassword': '' }
			});
			this.validateAll();

		} else if (nextProps.account.areCredentialsValid === false) {

			const errorMessage = errors[nextProps.account.checkCredentialsErrorid] ||
			localize('account-error-unknown');

			this.setState({
				credentialsChecked: false,
				unlocking: false,
				errors: { 'currentPassword': errorMessage }
			});
			focusSelect(this.currentPassword.current);
		}
	}

	handleEmailUpdate(nextProps) {
		if (nextProps.account.accountEmailUpdateSuccess === true) {
			this.setState(prevState => ({
				...prevState,
				oldEmail: this.state.currentEmail,
				currentEmail: this.state.newEmail,
				changingEmail: false,
				newEmailSuccess: true,
				errors: { ...prevState.errors, 'newEmail': null },
				touched: { ...prevState.touched, 'newEmail': null }				
			}), () => this.showNewEmailSentConfirmationPopup(this.state.newEmail, this.state.currentEmail));

		} else if (nextProps.account.accountEmailUpdateSuccess === false) {

			const errorMessage = errors[nextProps.account.accountEmailUpdateErrorId] ||
			localize('account-error-unknown');

			this.setState(prevState => ({
				...prevState,
				changingEmail: false,
				errors: { ...prevState.errors, 'newEmail': errorMessage }				
			}));			
			focusSelect(this.newEmail.current);
		}		
	}

	handleNameUpdate(nextProps) {
		const firstNameFiltered = this.getFilteredName('newFirstName');
		const lastNameFiltered = this.getFilteredName('newLastName');

		if (nextProps.account.accountNameUpdateSuccess === true) {

			this.setState(prevState => ({
				...prevState,
				newFirstName: firstNameFiltered,
				newLastName: lastNameFiltered,				
				changingName: false,
				newNameSuccess: true,
				errors: { ...prevState.errors, 'newLastName': null },
				touched: { ...prevState.touched, 'newFirstName': null,'newLastName': null }
			}));	

		} else if (nextProps.account.accountNameUpdateSuccess === false) {
			const errorMessage = errors[nextProps.account.accountNameUpdateErrorId] ||
			localize('account-error-unknown');

			this.setState(prevState => ({
				...prevState,
				newFirstName: firstNameFiltered,
				newLastName: lastNameFiltered,
				changingName: false,
				errors: { ...prevState.errors, 'newLastName': errorMessage }				
			}));

			focusSelect(this.newFirstName.current);
		}			
	}

	handlePasswordUpdate(nextProps) {
		if (nextProps.account.accountPasswordUpdateSuccess === true) {

			this.setState(prevState => ({
				...prevState,
				changingPassword: false,
				checkedPassword: this.state.newPassword,
				newPassword: '',
				confirmNewPassword: '',
				newPasswordSuccess: true,
				errors: { ...prevState.errors, 'confirmNewPassword': null },
				touched: { ...prevState.touched, 'newPassword': null, 'confirmNewPassword': null }
			}));	

		} else if (nextProps.account.accountPasswordUpdateSuccess === false) {
			const errorMessage = errors[nextProps.account.accountPasswordUpdateErrorId] ||
			localize('account-error-unknown');

			this.setState(prevState => ({
				...prevState,
				changingPassword: false,
				errors: { ...prevState.errors, 'confirmNewPassword': errorMessage }				
			}));

			focusSelect(this.newPassword.current);
		}			
	}

	handlePasswordReset(nextProps) {
		if (nextProps.account.accountPasswordResetSuccess === true) {
			this.setState(prevState => ({
				...prevState,
				showResetPasswordSuccess: true
			}));

		} else if (nextProps.account.accountPasswordResetSuccess === false) {
			this.setState(prevState => ({
				...prevState,
				showResetPasswordError: true
			}));
		}			
	}	
	
	componentWillReceiveProps(nextProps) {
		// Credentials check
		if (this.props.account.areCredentialsValid !== nextProps.account.areCredentialsValid) {
			this.handleCredentialsValidation(nextProps);
		}

		if (this.props.account.accountEmailUpdateSuccess !== nextProps.account.accountEmailUpdateSuccess) {
			this.handleEmailUpdate(nextProps);
		}		

		if (this.props.account.accountNameUpdateSuccess !== nextProps.account.accountNameUpdateSuccess) {
			this.handleNameUpdate(nextProps);
		}			

		if (this.props.account.accountPasswordUpdateSuccess !== nextProps.account.accountPasswordUpdateSuccess) {
			this.handlePasswordUpdate(nextProps);
		}			
		if (this.props.account.accountPasswordResetSuccess !== nextProps.account.accountPasswordResetSuccess) {
			this.handlePasswordReset(nextProps);
		}			

		const newState = {};
		if (this.props.user.firstName !== nextProps.user.firstName) {
			newState.newFirstName = nextProps.user.firstName;
		}
		if (this.props.user.lastName !== nextProps.user.lastName) {
			newState.newLastName = nextProps.user.lastName;
		}

		if (!_.isEmpty(newState)) {
			this.setState(prevState => ({
				...prevState,
				...newState
			}));
		}
	}

	onChange(event, name) {
		name === 'newEmail' && this.setState({ newEmailSuccess: false });
		name === 'newFirstName' || name === 'newLastName' && this.setState({ newNameSuccess: false });
		name === 'newPassword' && this.setState({ newPasswordSuccess: false });

		this.setFieldState(name, event.target.value);
	}

	setFieldState(fieldName, value) {
		this.setState({[fieldName]: value}, this.validateAll);
	}

	onBlur(event, name) {
		if (name === 'currentPassword') return;
		this.setState(prevState => ({
			...prevState,
			touched: { ...prevState.touched, [name]: true }
		}), this.validateAll);
	}

	onKeyPress(event, name) {
		if (event.key === 'Enter') {
			if (name === 'currentPassword') {
				this.unlockForm();
			} else if (name === 'newEmail') {
				this.updateEmail();
			} else if (name === 'newFirstName' || name === 'newLastName') {
				this.updateName();
			} else if (name === 'newPassword' || name === 'confirmNewPassword') {
				this.updatePassword();
			}
		}
	}

	validateAll() {
		let errors = {};
		for (let field in ValidationSchema) {
			let validate = ValidationSchema[field];
			if (validate) {
				let error = validate(this.state[field], this.state, this.props);
				if (error) {
					errors[field] = error;
				}
			}
		}
		this.setState({ errors });
	}

	hasUnsavedData() {

		const wasNameChanged = (this.state.newFirstName !== _.get(this.props.user, 'firstName') ||
		this.state.newLastName !== _.get(this.props.user, 'lastName'));

		const wasPasswordChanged = this.state.newPassword.length > 0 || this.state.confirmNewPassword.length > 0;

		return wasNameChanged || wasPasswordChanged ||
		this.state.newEmail !== this.state.currentEmail;
	}

	doIfCanLeave(callback) {
		if (!this.hasUnsavedData()) {
			callback(true);
			return;
		}

		this.props.showModal('CONFIRMATION_MODAL', {
			message: onLeaveMessage,
			buttons: [
				<Button
					key='button-cancel'
					variant='outlined'
					color='primary'
					data-tid="button-account-stay"
					onClick={() => {callback(false);}}
				>
					{localize('stay-on-page')}
				</Button>,
				<Button
					key='button-confirm'
					data-tid="button-account-leave"
					variant='contained'
					color='primary'
					onClick={() => {callback(true);}}
				>
					{localize('leave-page')}
				</Button>
			]
		});
	}

	onBeforeUnload(e) {
		if (this.hasUnsavedData()) {
			let dialogText = onLeaveMessage;
			e.returnValue = dialogText;
			return dialogText;
		}
	}

	field(name, placeholder, disabled, type, maxLength) {
		const { classes } = this.props;
		const errorClasses = { root: classes.root, error: classes.error };
		return (
			<FormField
				value={this.state[name]}
				className={classes.field}
				data-tid={`form-account-profile-${name}`}
				onChange={event => this.onChange(event, name)}
				onBlur={event => this.onBlur(event, name)}
				onKeyPress={event => this.onKeyPress(event, name)}
				id={name}
				placeholder={placeholder}
				{...(type ? { type } : {})}
				margin="normal"
				helperText={this.state.errors[name]}
				error={!!this.state.errors[name]}
				FormHelperTextProps={{ classes: errorClasses }}
				disabled={disabled}
				maxLength={maxLength}
				inputRef={this[name]}
			/>
		);
	}

	unlockForm() {
		this.setState({
			unlocking: true
		});

		this.props.checkCredentials(this.state.currentEmail, this.state.currentPassword);
	}

	updateEmail() {
		this.setState({
			oldEmail: '',
			changingEmail: true,
			errors: { 'newEmail': null }
		});

		this.props.updateEmail(this.state.currentEmail, this.state.checkedPassword, this.state.newEmail);
	}

	updateName() {
		this.setState({
			changingName: true,
			errors: { 'newLastName': null }
		});

		const firstNameFiltered = this.getFilteredName('newFirstName');
		const lastNameFiltered = this.getFilteredName('newLastName');

		this.props.updateName(this.state.currentEmail, this.state.checkedPassword, firstNameFiltered, lastNameFiltered);
	}

	updatePassword() {
		this.setState({
			changingPassword: true,
			errors: { 'newPassword': null }
		});

		this.props.updatePassword(
			this.state.currentEmail,
			this.state.checkedPassword,
			this.state.newPassword,
			this.state.confirmNewPassword
		);
	}

	getFilteredName(name) {
		return replaceConsecutiveSpaces((_.get(this.state, `[${name}]`) || '').trim());
	}

	showSetPasswordPopup() {
		this.props.showModal('SET_PASSWORD_MODAL', {
			title: localize('account-password-popup-title'),
			message: {
				__html: localize('account-password-popup-message', this.state.currentEmail)
			} ,
			buttons: [
				<Button
					key='set-password-button'
					data-tid="button-set-password-send-email"
					variant='contained'
					color='primary'
					onClick={() => this.resetPassword()}
				>
					{localize('account-password-popup-button')}
				</Button>
			]
		});
	}

	resetPassword() {
		const qs = makeQuery({
			host: getHost(),
			email: this.state.currentEmail,
			'set-password': true
		});

		this.props.resetPassword(qs);
	}

	showNewEmailSentConfirmationPopup(newEmail, oldEmail) {
		this.props.showModal('CONFIRM_YOUR_EMAIL_ADDRESS_MODAL', {
			title: localize('account-confirm-email-popup-title'),
			message: {
				__html: localize('account-confirm-email-popup-message', [newEmail, oldEmail])
			} ,
			buttons: [
				<Button
					key='new-email-sent-confirmation-button'
					data-tid="button-new-email-sent-confirmation"
					variant='contained'
					color='primary'
				>
					{localize('close')}
				</Button>
			]
		});		
	}

	render() {
		const { classes } = this.props;
		const passwordLocked = this.state.credentialsChecked || !this.state.currentPassword.length || this.state.unlocking;
		const emailLocked = !this.state.credentialsChecked || this.state.changingEmail;
		const nameLocked = !this.state.credentialsChecked || this.state.changingName;
		const changePasswordLocked = !this.state.credentialsChecked || this.state.changingPassword;
		
		if (!this.state.hasPassword) {
			if (this.state.showResetPasswordSuccess || this.state.showResetPasswordError) {
				this.props.hideModal();
				return (<CenteredContent>
					<StickyHeader>
						<Header className={classes.header}>{localize('account-header')}</Header>
					</StickyHeader>
					<Grid container className={classes.detailsContainer}>
						<Grid container direction="column">
							<Typography className={classes.emailSentMessage} variant="body2">
								{this.state.showResetPasswordSuccess
									?
									localize('account-reset-password-success')
									:
									localize('account-reset-password-error')
								}
							</Typography>
						</Grid>
					</Grid>
				</CenteredContent>);
			} else {
				this.showSetPasswordPopup();
				return null;
			}
		} else if (this.state.newEmailSuccess === true) {
			this.props.hideModal();
			return (<CenteredContent>
				<StickyHeader>
					<Header className={classes.header}>{localize('account-header')}</Header>
				</StickyHeader>
				<Grid container className={classes.detailsContainer}>
					<Grid container direction="column">
						<Typography className={classes.emailSentMessage} variant="body2">
							{localize('account-new-email-success')}
						</Typography>
					</Grid>
				</Grid>
			</CenteredContent>);
		}

		return (<CenteredContent>
			<StickyHeader>
				<Header className={classes.header}>{localize('account-header')}</Header>
			</StickyHeader>
			<Grid container className={classes.detailsContainer}>
				<Grid container direction="column">
					<Typography className={classes.fieldDescription} variant="body2">
						{localize('account-current-password-description')}
					</Typography>

					<Grid container direction="row">
						<Grid item className={classes.columnField}>
							{ this.field(
								'currentPassword',
								localize('account-current-password-placeholder'),
								this.state.credentialsChecked || this.state.unlocking,
								'password')
							}
						</Grid>
						<Grid item className={classes.columnSaveButton}>
							<Button
								variant='contained'
								color='primary'
								data-tid="button-account-unlock-form"
								className={classes.button}
								disabled={passwordLocked}
								onClick={() => this.unlockForm()}
							>
								{
									this.state.unlocking ?
										<SpinnerIcon className={classes.spinnerIcon} /> :
										localize('account-current-password-button')
								}
							</Button>					
						</Grid>
					</Grid>
				</Grid>

				<Grid container direction="column">
					<FormLabel>
						{localize('account-email-label')}
						{this.state.newEmailSuccess && <CheckIcon className={classes.checkIcon} />}
					</FormLabel>
					<Grid container direction="row">
						<Grid item className={classes.columnField}>
							{ this.field('newEmail', localize('account-email-placeholder'), emailLocked)}
						</Grid>
						<Grid item className={classes.columnSaveButton}>
							<Button
								variant='contained'
								color='primary'
								data-tid="button-account-update-email"
								className={classes.button}
								onClick={() => this.updateEmail()}
								disabled={emailLocked || _.get(this.state.errors, 'newEmail')}>
								{
									this.state.changingEmail ?
										<SpinnerIcon className={classes.spinnerIcon} /> :
										localize('account-email-button')
								}
							</Button>
						</Grid>
					</Grid>
				</Grid>

				<Grid container direction="column">
					<FormLabel>
						{localize('account-name-label')}
						{this.state.newNameSuccess && <CheckIcon className={classes.checkIcon} />}
					</FormLabel>
					<Grid container direction="row">
						<Grid item container direction="column" className={classes.columnField}>
							<Grid item>
								{ this.field('newFirstName', localize('account-name-first-placeholder'), nameLocked)}
							</Grid>
							<Grid item>
								{ this.field('newLastName', localize('account-name-last-placeholder'), nameLocked)}
							</Grid>
						</Grid>
						<Grid item className={classes.columnSaveButton}>
							<Button
								variant='contained'
								color='primary'
								data-tid="button-account-update-name"
								className={classes.button}
								onClick={() => this.updateName()}
								disabled={
									nameLocked ||
									_.get(this.state.errors, 'newFirstName') ||
									_.get(this.state.errors, 'newLastName')
								}>
								{
									this.state.changingName ?
										<SpinnerIcon className={classes.spinnerIcon} /> :
										localize('account-name-button')
								}								
							</Button>

						</Grid>
					</Grid>
				</Grid>

				<Grid container direction="column">
					<FormLabel>
						{localize('account-password-label')}
						{this.state.newPasswordSuccess && <CheckIcon className={classes.checkIcon} />}
					</FormLabel>
					<Typography className={classes.fieldDescription} variant="body2">
						{localize('account-password-description', [minPasswordLength, maxPasswordLength])}
					</Typography>
					<Grid container direction="row">
						<Grid item className={classes.columnField}>
							{ this.field(
								'newPassword',
								localize('account-password-placeholder'),
								changePasswordLocked,
								'password',
								maxPasswordLength)
							}
							{ this.field(
								'confirmNewPassword',
								localize('account-password-repeat-placeholder'),
								changePasswordLocked,
								'password',
								maxPasswordLength)
							}
						</Grid>
						<Grid item className={classes.columnSaveButton}>
							<Button
								variant='contained'
								color='primary'
								data-tid="button-account-update-password"
								className={classes.button}
								onClick={() => this.updatePassword()}
								disabled={
									changePasswordLocked ||
									_.get(this.state.errors, 'newPassword') ||
									_.get(this.state.errors, 'confirmNewPassword')
								}>
								{
									this.state.changingPassword ?
										<SpinnerIcon className={classes.spinnerIcon} /> :
										localize('account-password-button')
								}									
							</Button>
						</Grid>
					</Grid>

				</Grid>
			</Grid>
		</CenteredContent>);
	}
}

const mapStateToProps = state => ({
	companies: state.companies,
	isAdmin: isAdmin(state),
	isNameValid: state.companies.valid,
	user: state.session.user,
	account: state.account
});

const mapDispatchToProps = dispatch => bindActionCreators({
	updateName,
	updatePassword,
	updateEmail,
	resetPassword,
	checkCredentials,
	showModal,
	hideModal
}, dispatch);

AccountDetails.propTypes = {
	classes: PropTypes.object,
	cloudFolders: PropTypes.object,
	updateName: PropTypes.func.isRequired,
	updateEmail: PropTypes.func.isRequired,
	resetPassword: PropTypes.func.isRequired,
	checkCredentials: PropTypes.func.isRequired,
	updatePassword: PropTypes.func.isRequired,
	isAdmin: PropTypes.bool,
	showModal: PropTypes.func,
	hideModal: PropTypes.func,
	isNameValid: PropTypes.bool,
	user: PropTypes.object.isRequired,
	account: PropTypes.object.isRequired,
	companies: PropTypes.object,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(AccountDetails);


// WEBPACK FOOTER //
// ./src/containers/AccountDetails/AccountDetails.js